/**
 * Slideshow Component
 * Initializes Swiper instances based on configuration data passed from app.js.
 */

import Swiper from 'swiper';
// Import necessary Swiper modules
import { Autoplay, Navigation, Pagination, Controller, EffectFade, Thumbs, Manipulation } from 'swiper/modules';
// Register the modules with Swiper
Swiper.use([Navigation, Pagination, Autoplay, Controller, EffectFade, Thumbs, Manipulation]);

// Import CountUp for stat card slideshows
import { CountUp } from 'countup.js';

// Export the main initialization function
export function slideshows(slideshowConfigs, log = false) {

    // --- 1. Initialize Swipers based on passed configurations ---

    // Check if Swiper library is available
    if (typeof Swiper === 'undefined') {
        console.error('Swiper library is not loaded.');
        return;
    }

    // Check if valid configurations were passed
    if (!slideshowConfigs || !Array.isArray(slideshowConfigs) || slideshowConfigs.length === 0) {
        if (log) console.log('No slideshow configurations provided to initialize.');
        // Don't return here yet, as other slideshow types might exist below
    } else {
        if (log) console.log('Initializing Swipers from configurations:', slideshowConfigs);

        // Iterate through each configuration object passed from WordPress/PHP
        slideshowConfigs.forEach(function(slideshow) {
            // Basic validation for the config object
            if (!slideshow || typeof slideshow !== 'object' || !slideshow.id) {
                console.warn('Invalid slideshow configuration object:', slideshow);
                return; // Skip this invalid config
            }

            const slideshow_id_selector = '#' + slideshow.id + ' .swiper'; // Selector for the Swiper container
            const slideshow_type = slideshow.type;
            const slideshow_settings = slideshow.settings || {}; // Default to empty object if settings are missing

            // Ensure modules are included in settings (might be redundant but safe)
            slideshow_settings.modules = [Autoplay, Navigation, Pagination, Controller, EffectFade, Thumbs, Manipulation];

            // --- Configure Navigation ---
            // Check if navigation is explicitly enabled in settings
            if (slideshow_settings.navigation === true) {
                slideshow_settings.navigation = {
                    nextEl: '#' + slideshow.id + ' .swiper__nav--next', // Standardized selector
                    prevEl: '#' + slideshow.id + ' .swiper__nav--prev', // Standardized selector
                };
            } else {
                // Ensure navigation is explicitly disabled if not true
                slideshow_settings.navigation = false;
            }

            // --- Configure Pagination ---
            // Check if pagination settings exist
            if (typeof slideshow_settings.pagination === 'object' && slideshow_settings.pagination !== null) {
                // Set the element selector dynamically based on the slideshow ID
                slideshow_settings.pagination.el = '#' + slideshow.id + ' .swiper__pagination'; // Standardized selector

                // Make bullets clickable if that's the pagination type
                if (slideshow_settings.pagination.type === 'bullets') {
                    slideshow_settings.pagination.clickable = true;
                }
                // Add other pagination type handling if needed (e.g., fraction)
                if (slideshow_settings.pagination.type === 'fraction') {
                     slideshow_settings.pagination.el = '#' + slideshow.id + ' .swiper__fraction'; // Use specific class if needed
                }

            } else {
                 // Ensure pagination is explicitly disabled if no config object provided
                 slideshow_settings.pagination = false;
            }


            // --- Handle different slideshow types ---

            // Special handling for "content-switcher" type (responsive init/destroy)
            if (slideshow_type === 'content-switcher') {
                handleContentSwitcher(slideshow, slideshow_id_selector, slideshow_settings, log);
            }
            // Handle slideshows that are only active on mobile
            else if (slideshow_type === 'mobile-only') {
                handleMobileSlideshow(slideshow, slideshow_id_selector, slideshow_settings, log);
            }
            // Handle 'basic' or other types that just need standard initialization
            else {
                // Check if the Swiper container element exists
                const swiperElement = document.querySelector(slideshow_id_selector);
                if (swiperElement) {
                    // Check if a Swiper instance is already attached to this element
                    if (swiperElement.swiper) {
                        if (log) console.warn(`Swiper instance already exists on ${slideshow_id_selector}. Skipping re-initialization. You might want to ensure configurations are unique or destroy old instances if re-init is intended.`);
                        // Optionally, if re-initialization with new settings is sometimes intended, destroy the old one first:
                        // try { swiperElement.swiper.destroy(true, true); } catch (e) { console.error('Error destroying previous swiper instance:', e); }
                        // new Swiper(swiperElement, slideshow_settings); // Then reinitialize
                    } else {
                        try {
                            new Swiper(swiperElement, slideshow_settings);
                            if (log) console.log(`Standard Swiper initialized for: ${slideshow_id_selector}`);
                        } catch (error) {
                            console.error(`Error initializing standard Swiper for ${slideshow_id_selector}:`, error, slideshow_settings);
                        }
                    }
                } else {
                    if (log) console.warn(`Swiper container not found for standard init: ${slideshow_id_selector}`);
                }
            }
        }); // End foreach slideshowConfig
    } // End if slideshowConfigs exist


    // --- 2. Initialize Card Galleries (.layout__cards--6) ---
    // This logic seems independent of the passed configs and finds elements in the DOM.
    const cardGalleries = document.querySelectorAll('.layout__cards--6');
    cardGalleries.forEach(cardGallery => {
        initializeCardGallery(cardGallery, log);
    });


    // --- 3. Initialize Stat Card Slideshows (.cards--2) ---
    // This logic also seems independent and finds elements in the DOM.
    const statCardSlideshows = document.querySelectorAll('.cards--2');
    statCardSlideshows.forEach(function(slideshow) {
        initializeStatCardSlideshow(slideshow, log);
    });



    // --- Final Log ---
    if (log) console.log('slideshows component finished initialization.');
} // End export function slideshows


// --- Helper function for Content Switcher Type ---
function handleContentSwitcher(slideshow, slideshow_id_selector, slideshow_settings, log) {
    let the_slideshow = null; // Will hold Swiper instance if active
    const container = document.getElementById(slideshow.id);
    if (!container) {
        if (log) console.warn(`Container element not found for content-switcher: #${slideshow.id}`);
        return;
    }
    const detailsElements = container.querySelectorAll('details'); // Used for mobile accordion behavior

    // Configure custom bullet rendering using <summary> text
    // Ensure pagination object exists before modifying
    slideshow_settings.pagination = slideshow_settings.pagination || {};
    slideshow_settings.pagination.el = '#' + slideshow.id + ' .swiper-pagination'; // Ensure element is set
    slideshow_settings.pagination.clickable = true; // Bullets should be clickable
    slideshow_settings.pagination.type = 'bullets'; // Force bullets for this render function
    slideshow_settings.pagination.renderBullet = function(bulletIndex, className) {
        let bulletName = `Slide ${bulletIndex + 1}`; // Default name
        // Use slideshow_id_selector to find summaries within the correct swiper instance
        const summaryElements = document.querySelectorAll(slideshow_id_selector + ' .swiper-slide summary');
        if (summaryElements[bulletIndex]) {
            bulletName = summaryElements[bulletIndex].innerText || bulletName;
        }
        return `<li class="${className}">${bulletName}</li>`;
    };

    /**
     * Initialize or destroy Swiper depending on viewport width
     */
    function initOrDestroySlideshow() {
        const wideEnough = window.innerWidth >= 1080; // Breakpoint for switching behavior
        const swiperElement = document.querySelector(slideshow_id_selector);

        if (!swiperElement) {
             if (log) console.warn(`Swiper element not found for content-switcher: ${slideshow_id_selector}`);
             return;
        }

        if (wideEnough) {
            // If viewport is wide enough, create Swiper if it doesn't exist
            if (!the_slideshow || the_slideshow.destroyed) { // Check if null or destroyed
                try {
                    the_slideshow = new Swiper(swiperElement, slideshow_settings);
                    if (log) console.log(`Content-switcher Swiper initialized for: ${slideshow_id_selector}`);
                } catch (error) {
                     console.error(`Error initializing content-switcher Swiper for ${slideshow_id_selector}:`, error, slideshow_settings);
                     return; // Don't proceed if init failed
                }
            } else {
                // If it exists, just update it (might be needed if content changed)
                the_slideshow.update();
            }

            // Ensure <details> elements are open on desktop
            detailsElements.forEach((detail) => {
                if (!detail.hasAttribute('open')) {
                    detail.setAttribute('open', ''); // Set attribute without value
                }
            });
        } else {
            // If viewport is too narrow, destroy Swiper if it exists
            if (the_slideshow && !the_slideshow.destroyed) {
                try {
                    the_slideshow.destroy(true, true); // Destroy instance and remove listeners
                    the_slideshow = null; // Reset variable
                    if (log) console.log(`Content-switcher Swiper destroyed for: ${slideshow_id_selector}`);
                } catch (error) {
                     console.error(`Error destroying content-switcher Swiper for ${slideshow_id_selector}:`, error);
                }
            }
             // Ensure <details> elements are closed on mobile (allow user toggle)
             // We don't force them closed here, respecting user interaction.
             // If you *always* want them closed on mobile init, uncomment below:
             /*
             detailsElements.forEach((detail) => {
                 detail.removeAttribute('open');
             });
             */
        }
    }

    // Run check immediately on load
    initOrDestroySlideshow();

    // Add event listeners for resize and orientation changes
    window.addEventListener('resize', initOrDestroySlideshow);
    window.addEventListener('orientationchange', initOrDestroySlideshow);
}

/**
 * Helper function for slideshows that are only active on mobile viewports.
 * It initializes Swiper below a certain breakpoint and destroys it above.
 */
function handleMobileSlideshow(slideshow, slideshow_id_selector, slideshow_settings, log) {
    let the_slideshow = null;
    const container = document.getElementById(slideshow.id);
    if (!container) {
        if (log) console.warn(`Container element not found for mobile-slideshow: #${slideshow.id}`);
        return;
    }

    // Get breakpoint from settings, default to 640px (the theme's $breakpoint-xs)
    const breakpoint = slideshow_settings.breakpoint || 640;

    /**
     * Initialize or destroy Swiper depending on viewport width
     */
    function initOrDestroySlideshow() {
        const isMobile = window.innerWidth <= breakpoint;
        const swiperElement = document.querySelector(slideshow_id_selector);

        if (!swiperElement) {
            if (log) console.warn(`Swiper element not found for mobile-slideshow: ${slideshow_id_selector}`);
            return;
        }

        if (isMobile) {
            // If viewport is mobile, create Swiper if it doesn't exist
            if (!the_slideshow || the_slideshow.destroyed) {
                try {
                    the_slideshow = new Swiper(swiperElement, slideshow_settings);
                    if (log) console.log(`Mobile-only Swiper initialized for: ${slideshow_id_selector}`);
                } catch (error) {
                    console.error(`Error initializing mobile-only Swiper for ${slideshow_id_selector}:`, error, slideshow_settings);
                    return;
                }
            } else {
                the_slideshow.update();
            }
        } else {
            // If viewport is desktop, destroy Swiper if it exists
            if (the_slideshow && !the_slideshow.destroyed) {
                try {
                    the_slideshow.destroy(true, true);
                    the_slideshow = null;
                    if (log) console.log(`Mobile-only Swiper destroyed for: ${slideshow_id_selector}`);
                } catch (error) {
                    console.error(`Error destroying mobile-only Swiper for ${slideshow_id_selector}:`, error);
                }
            }
        }
    }

    // Run check immediately on load
    initOrDestroySlideshow();

    // Add event listeners for resize and orientation changes
    window.addEventListener('resize', initOrDestroySlideshow);
    window.addEventListener('orientationchange', initOrDestroySlideshow);
}


// --- Helper function for Card Gallery Type (.layout__cards--6) ---
function initializeCardGallery(cardGallery, log) {
    const cards = cardGallery.querySelectorAll('.card');
    const swiperContainer = cardGallery.querySelector('.modal-gallery');

    if (!swiperContainer || cards.length === 0) {
        if (log) console.log('Card gallery elements not found for:', cardGallery.id || 'unknown gallery');
        return;
    }

    let mainSwiper = null;
    let thumbsSwiper = null;

    // Store original slides (as HTML strings) for re-use
    const mainSwiperElement = swiperContainer.querySelector('.swiper-main');
    const thumbsSwiperElement = swiperContainer.querySelector('.swiper-thumbs');

    if (!mainSwiperElement || !thumbsSwiperElement) {
         if (log) console.log('Main or thumbs swiper container not found within modal-gallery for:', cardGallery.id);
         return;
    }

    const originalMainSlides = Array.from(mainSwiperElement.querySelectorAll('.swiper-slide')).map(slide => slide.outerHTML);
    const originalThumbSlides = Array.from(thumbsSwiperElement.querySelectorAll('.swiper-slide')).map(slide => slide.outerHTML);

    // Attach click + keyboard events to each card
    cards.forEach(card => {
        card.addEventListener('click', () => {
            const slidesData = card.dataset.slides;
            if (!slidesData) {
                console.warn('Card is missing data-slides attribute:', card);
                return;
            }
            const slideIndices = slidesData.split(',').map(Number);

            if (!mainSwiper || mainSwiper.destroyed) { // Check if null or destroyed
                // Initialize Swiper on the first click
                initializeLinkedSwipers(swiperContainer, slideIndices, log);
            } else {
                // Filter slides for subsequent clicks
                filterSlides(mainSwiper, thumbsSwiper, slideIndices, originalMainSlides, originalThumbSlides, log);
            }
            // Optionally open the modal containing the swiper here
            // e.g., MicroModal.show(swiperContainer.id);
        });

        card.addEventListener('keydown', event => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault(); // Prevent default spacebar scroll
                card.click();
            }
        });
    });

    // Initialize the two linked Swipers
    function initializeLinkedSwipers(container, initialSlides, log) {
        const thumbsSelector = '#' + container.id + ' .swiper-thumbs';
        const mainSelector = '#' + container.id + ' .swiper-main';
        const nextNavSelector = '#' + container.id + ' .swiper__nav--next';
        const prevNavSelector = '#' + container.id + ' .swiper__nav--prev';

        try {
            // Thumbs swiper
            thumbsSwiper = new Swiper(thumbsSelector, {
                modules: [Thumbs, Manipulation], // Ensure modules are included
                slidesPerView: 6, // Adjust as needed
                spaceBetween: 10,
                // centeredSlides: true, // Can cause issues with filtering, consider removing if problematic
                loop: false, // Loop is difficult with dynamic filtering
                slideToClickedSlide: true,
                watchSlidesProgress: true,
            });
            if (log) console.log(`Thumbs Swiper initialized for: ${thumbsSelector}`);

            // Main swiper
            mainSwiper = new Swiper(mainSelector, {
                modules: [Navigation, Thumbs, Manipulation], // Ensure modules are included
                direction: 'horizontal',
                loop: false, // Loop is difficult with dynamic filtering
                speed: 300,
                slidesPerView: 1,
                spaceBetween: 0,
                initialSlide: 0,
                keyboard: true, // Enable keyboard navigation
                autoHeight: false, // Set true if slide heights vary significantly
                navigation: {
                    nextEl: nextNavSelector,
                    prevEl: prevNavSelector,
                },
                thumbs: {
                    swiper: thumbsSwiper // Link thumbs to main
                },
                on: {
                    // Use 'init' event which fires after initialization
                    init: function(swiperInstance) {
                        // Filter slides *after* initialization is fully complete
                        // Use a small timeout to ensure DOM is ready if needed
                        setTimeout(() => {
                            filterSlides(swiperInstance, thumbsSwiper, initialSlides, originalMainSlides, originalThumbSlides, log);
                        }, 100); // Adjust timeout if needed
                    },
                    // slideChange: function(swiperInstance) {
                    //     // Thumbs should sync automatically via the thumbs module
                    // }
                }
            });
             if (log) console.log(`Main Swiper initialized for: ${mainSelector}`);

        } catch (error) {
            console.error(`Error initializing linked Swipers for gallery ${container.id}:`, error);
            // Clean up if one failed
            if (thumbsSwiper && !thumbsSwiper.destroyed) thumbsSwiper.destroy();
            if (mainSwiper && !mainSwiper.destroyed) mainSwiper.destroy();
            mainSwiper = null;
            thumbsSwiper = null;
        }
    }

    // Filter slides by removing & re-adding, using Manipulation module
    function filterSlides(mainSwiperInstance, thumbsSwiperInstance, slideIndices, originalMains, originalThumbs, log) {
        if (!mainSwiperInstance || mainSwiperInstance.destroyed || !thumbsSwiperInstance || thumbsSwiperInstance.destroyed) {
             if (log) console.warn('Attempted to filter slides but Swiper instance(s) are missing or destroyed.');
             return;
        }

        // Ensure we have an array of numbers
        const indicesArray = Array.isArray(slideIndices) ? slideIndices : slideIndices.map(Number);

        if (log) console.log(`Filtering slides to indices: ${indicesArray.join(', ')}`);

        try {
            // Remove all slides safely
            mainSwiperInstance.removeAllSlides();
            thumbsSwiperInstance.removeAllSlides();

            // --- Re-append slides ---
            // Filter original slide HTML based on the desired indices (adjusting for 0-based index)
            const slidesToAppendMain = indicesArray
                .map(index => originalMains[index - 1]) // index-1 because indices are 1-based
                .filter(Boolean); // Remove any undefined entries if index was out of bounds

            const slidesToAppendThumbs = indicesArray
                .map(index => originalThumbs[index - 1]) // index-1
                .filter(Boolean);

            // Append the filtered slides
            if (slidesToAppendMain.length > 0) {
                mainSwiperInstance.appendSlide(slidesToAppendMain);
            }
            if (slidesToAppendThumbs.length > 0) {
                thumbsSwiperInstance.appendSlide(slidesToAppendThumbs);
            }

            // --- Update and Reset ---
            mainSwiperInstance.update();
            thumbsSwiperInstance.update();

            // Go to the first slide after filtering
            mainSwiperInstance.slideTo(0, 0); // Slide instantly
            thumbsSwiperInstance.slideTo(0, 0);

            // --- Adjust Thumbs Visibility ---
            // Hide thumbs container if only one slide remains
            if (thumbsSwiperInstance.el) { // Check if element exists
                 thumbsSwiperInstance.el.style.display = indicesArray.length <= 1 ? 'none' : ''; // Use empty string to reset to default/CSS display
            }

             if (log) console.log(`Slides filtered. Main: ${slidesToAppendMain.length}, Thumbs: ${slidesToAppendThumbs.length}`);

        } catch (error) {
            console.error('Error during slide filtering:', error);
            // Attempt to recover or log more details
            console.error('Main Swiper state:', mainSwiperInstance);
            console.error('Thumbs Swiper state:', thumbsSwiperInstance);
        }
    }
}


// --- Helper function for Stat Card Slideshow Type (.cards--2) ---
function initializeStatCardSlideshow(slideshow, log) {
    if ( !slideshow.id ) { return; }
    const swiperSelector = '#' + slideshow.id + ' .swiper'; // Use the ID of the .cards--2 element
    const swiperElement = document.querySelector(swiperSelector);

    if (!swiperElement) {
        if (log) console.warn(`Swiper container not found for stat cards: ${swiperSelector}`);
        return;
    }

    try {
        const swiper = new Swiper(swiperElement, {
            modules: [Navigation, Autoplay], // Add modules needed
            init: false, // Initialize manually after setting up events
            effect: 'slide',
            loop: true, // Loop for continuous effect
            speed: 600,
            slidesPerView: 1, // Base slidesPerView
            watchSlidesProgress: true, // Needed to detect visible slides
            autoplay: {
                delay: 4000, // Adjust delay as needed
                disableOnInteraction: true,
                pauseOnMouseEnter: true
            },
            navigation: {
                nextEl: '#' + slideshow.id + ' .swiper__nav--next',
                prevEl: '#' + slideshow.id + ' .swiper__nav--prev'
            },
            pagination: false, // No pagination for this type usually
            breakpoints: { // Responsive adjustments
                861: { // Desktop
                    slidesPerView: 4,
                },
                641: { // Tablet
                    slidesPerView: 2,
                }
            }
        })
        .on('init', function(swiperInstance) { // Use 'init' event
            // Initial count-up for visible slides on load
            triggerCountUpForVisible(swiperInstance);
        })
        .on('slideChangeTransitionEnd', function(swiperInstance) {
            // Count-up for newly visible slides after transition
            triggerCountUpForVisible(swiperInstance);
        });

        // Manually initialize the Swiper instance
        swiper.init();
        if (log) console.log(`Stat card Swiper initialized for: ${swiperSelector}`);

    } catch (error) {
        console.error(`Error initializing stat card Swiper for ${swiperSelector}:`, error);
    }

    // Helper to trigger CountUp on visible slides
    function triggerCountUpForVisible(swiperInstance) {
        swiperInstance.slides.forEach(slide => {
            const theCounter = slide.querySelector('.card__counter');
            if (!theCounter) return; // Skip if no counter element

            // Check if slide is visible and hasn't been counted yet
            if (slide.classList.contains('swiper-slide-visible') && !slide.classList.contains('count-done')) {
                slide.classList.add('count-done'); // Mark as counted
                doSlideCountUp(theCounter);
            }
            // Optionally reset count if slide becomes invisible (if desired)
            else if (!slide.classList.contains('swiper-slide-visible') && slide.classList.contains('count-done')) {
                 slide.classList.remove('count-done');
                 // Reset counter text if needed: theCounter.textContent = theCounter.dataset.start || '0';
            }
        });
    }

    // Helper to perform the CountUp animation
    function doSlideCountUp(theCounter) {
        let decimalPlaces = 0;
        let separator = ',';
        const endValue = theCounter.dataset.end;
        const cardStatElement = theCounter.closest('.card__stat'); // Find parent stat element for options

        if (!endValue) {
             console.warn('Counter element missing data-end attribute:', theCounter);
             return;
        }

        // Check for separator option on parent data attribute
        if (cardStatElement && cardStatElement.dataset.separator === '0') {
            separator = '';
        }

        // Determine decimal places
        if (endValue.includes('.')) {
            decimalPlaces = endValue.split('.')[1].length;
        }

        // Initialize and start CountUp
        try {
            const countUp = new CountUp(theCounter, endValue, {
                // enableScrollSpy: true, // Scroll spy might interfere with Swiper visibility checks
                decimalPlaces: decimalPlaces,
                separator: separator,
                useEasing: true,
                duration: 2 // Animation duration in seconds
            });
            if (!countUp.error) {
                countUp.start();
            } else {
                console.error('CountUp error:', countUp.error);
                theCounter.textContent = endValue; // Fallback to showing end value directly
            }
        } catch (error) {
             console.error('Error initializing CountUp:', error);
             theCounter.textContent = endValue; // Fallback
        }
    }
}
